import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Calendar,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cargomatic/ui';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { getClassName } from '../../utils/ui/classes';

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';
// Accept a wide range of typed inputs so US, European, ISO, and dash-separated
// dates all parse without forcing the user to match the display format.
const FALLBACK_PARSE_FORMATS = [
  'MM/DD/YYYY',
  'M/D/YYYY',
  'M/D/YY',
  'MM/DD/YY',
  'DD/MM/YYYY',
  'D/M/YYYY',
  'YYYY-MM-DD',
  'YYYY/MM/DD',
  'MM-DD-YYYY',
  'M-D-YYYY',
  'DD-MM-YYYY',
];

function getParseFormats(dateFormat) {
  // Prioritize the locale's format, then fall back so users can still paste
  // ISO strings etc. without retyping.
  const ordered = [dateFormat, ...FALLBACK_PARSE_FORMATS];
  return Array.from(new Set(ordered));
}

function toDate(value) {
  if (!value) return undefined;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? undefined : value;
  if (moment.isMoment(value))
    return value.isValid() ? value.toDate() : undefined;
  const m = moment(value);
  return m.isValid() ? m.toDate() : undefined;
}

function formatValue(value, dateFormat) {
  const d = toDate(value);
  return d ? moment(d).format(dateFormat) : '';
}

/**
 * Drop-in-shaped replacement for the react-datepicker-based v2/DatePicker.
 * Same API: `selected`, `onChange(Date|null)`, `onChangeRaw(event)`,
 * `placeholderText`, `isClearable`, `className`. Internally renders the
 * @cargomatic/ui Calendar inside a Popover, with an Input you can type into.
 *
 * Typing still fires `onChangeRaw` on every keystroke so callers that route
 * raw text through `processDateTimeChange` (StopCompleteModal etc.) keep
 * working. `onChange` fires with a real Date on a successful parse or a
 * calendar click; with null on clear.
 */
const CargomaticDatePicker = ({
  selected,
  onChange,
  onChangeRaw,
  placeholderText,
  isClearable,
  className,
  dataTestId,
  dateFormat = DEFAULT_DATE_FORMAT,
}) => {
  const parseFormats = React.useMemo(
    () => getParseFormats(dateFormat),
    [dateFormat],
  );
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => formatValue(selected, dateFormat));
  const [calendarMonth, setCalendarMonth] = useState(
    () => toDate(selected) || new Date(),
  );
  const isUserEditing = useRef(false);

  // Sync external `selected` -> input text, but only when the user isn't
  // mid-typing. Otherwise every invalid keystroke would round-trip through the
  // parent (selected -> null) and wipe what they've typed.
  useEffect(() => {
    if (isUserEditing.current) return;
    setText(formatValue(selected, dateFormat));
  }, [selected, dateFormat]);

  // Keep the visible calendar month in sync with the selected date so typing
  // a far-off date (e.g. 04/03/1981) navigates the calendar there.
  useEffect(() => {
    const d = toDate(selected);
    if (d) setCalendarMonth(d);
  }, [selected]);

  const handleTextChange = (e) => {
    const value = e.target.value;
    isUserEditing.current = true;
    setText(value);
    onChangeRaw?.(e);
    if (!value) {
      onChange?.(null);
      return;
    }
    const m = moment(value, parseFormats, true);
    if (m.isValid()) {
      onChange?.(m.toDate());
    }
  };

  const handleBlur = () => {
    isUserEditing.current = false;
    // Reformat to the locale's canonical format if the typed text parses.
    if (text) {
      const m = moment(text, parseFormats, true);
      if (m.isValid()) {
        setText(m.format(dateFormat));
      }
    } else {
      setText(formatValue(selected, dateFormat));
    }
  };

  const handleCalendarSelect = (date) => {
    if (!date) {
      setText('');
      onChange?.(null);
    } else {
      setText(moment(date).format(dateFormat));
      onChange?.(date);
    }
    setOpen(false);
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setText('');
    onChange?.(null);
  };

  const selectedDate = toDate(selected);

  const containerClass = getClassName(
    {
      relative: true,
      'is-invalid':
        typeof className === 'object' && className && className.error,
    },
    typeof className === 'string' ? className : null,
  );

  return (
    <div className={containerClass} data-testid={dataTestId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
            aria-label="Open calendar"
            tabIndex={-1}
          >
            <CalendarIcon className="size-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            selected={selectedDate}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            onSelect={handleCalendarSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <Input
        type="text"
        value={text}
        onChange={handleTextChange}
        onFocus={() => {
          isUserEditing.current = true;
        }}
        onBlur={handleBlur}
        placeholder={placeholderText || dateFormat}
        className="pl-9 pr-8"
      />
      {isClearable && text && (
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
          aria-label="Clear date"
          onClick={handleClear}
          tabIndex={-1}
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
};

CargomaticDatePicker.propTypes = {
  selected: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.object, // moment
    PropTypes.string,
  ]),
  onChange: PropTypes.func,
  onChangeRaw: PropTypes.func,
  placeholderText: PropTypes.string,
  isClearable: PropTypes.bool,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  dataTestId: PropTypes.string,
  dateFormat: PropTypes.string,
};

export default CargomaticDatePicker;
